import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Trash2 } from "lucide-react";
import api from '../services/api';

const PackageRequest = () => {
  const [packageRequests, setPackageRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    id: null,
    name: ""
  });

  useEffect(() => {
    fetchPackageRequests();
  }, []);

  const fetchPackageRequests = async () => {
    try {
      const response = await api.get('/enquiries');
      setPackageRequests(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      console.error("Error fetching package requests:", err);
    }
  };

  const handleDelete = (id, name) => {
    setDeleteModal({
      isOpen: true,
      id,
      name
    });
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/enquiries/delete/${deleteModal.id}`);
      // Refresh the list after successful deletion
      await fetchPackageRequests();
      setDeleteModal({
        isOpen: false,
        id: null,
        name: ""
      });
    } catch (err) {
      console.error("Error deleting package request:", err);
      setError("Failed to delete package request");
    }
  };

  const cancelDelete = () => {
    setDeleteModal({
      isOpen: false,
      id: null,
      name: ""
    });
  };

  if (loading) {
    return (
      <motion.div
        className="max-w-7xl mx-auto p-6 mt-10 bg-slate-900 rounded-xl text-slate-200 shadow-2xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-2xl font-bold mb-6">Package Requests</h2>
        <div className="text-center py-10">Loading...</div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        className="max-w-7xl mx-auto p-6 mt-10 bg-slate-900 rounded-xl text-slate-200 shadow-2xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-2xl font-bold mb-6">Customer Quote Data</h2>
        <div className="text-center py-10 text-red-400">Error: {error}</div>
      </motion.div>
    );
  }

  return (
    <>
      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            className="bg-slate-800 rounded-lg p-6 max-w-md w-full text-white"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 className="text-xl font-bold mb-4">Confirm Deletion</h3>
            <p className="mb-6">
              Are you sure you want to delete the package request for{" "}
              <span className="font-semibold">{deleteModal.name}</span>?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 rounded-md bg-slate-700 hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Main Content */}
      <motion.div
        className="max-w-7xl mx-auto p-6 mt-10 bg-slate-900 rounded-xl text-slate-200 shadow-2xl overflow-x-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-2xl font-bold mb-6">Customer Quote Data</h2>

        {packageRequests.length === 0 ? (
          <div className="text-center py-10">No package requests found</div>
        ) : (
          <table className="min-w-full text-sm text-left border-collapse">
            <thead className="bg-slate-800 text-slate-400">
              <tr>
                <th className="p-3">S. No.</th>
                <th className="p-3">Name</th>
                <th className="p-3">Phone</th>
                <th className="p-3">Travel Date</th>
                <th className="p-3">Destination</th>
                <th className="p-3">Leaving From</th>
                <th className="p-3">Total Person</th>
                {/* <th className="p-3">No. of Days</th> */}
                <th className="p-3">Booking Within (days)</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {packageRequests.map((req, index) => (
                <tr key={req.id || index} className="border-b border-slate-700 hover:bg-slate-800">
                  <td className="p-3">{index + 1}</td>
                  <td className="p-3">{req.name || 'N/A'}</td>
                  <td className="p-3">{req.phone || 'N/A'}</td>
                  <td className="p-3">{req.travelDate.split('T')[0]}</td>
                  <td className="p-3">{req.destination || 'N/A'}</td>
                  <td className="p-3">{req.leavingFrom || 'N/A'}</td>
                  <td className="p-3">{req.totalPerson || 'N/A'}</td>
                  {/* <td className="p-3">{req.totalDays || 'N/A'}</td> */}
                  <td className="p-3">{req.bookingWithin || 'N/A'}</td>
                  <td className="p-3">
                    <button
                      onClick={() => handleDelete(req.id, req.name)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </motion.div>
    </>
  );
};

export default PackageRequest;